/**
 * Strips credentials out of URLs and free text before they reach Datadog.
 *
 * Two browser fetches in this app carry a signature in the URL, and each one
 * becomes a RUM event that holds that URL verbatim:
 *   - the battleground pressure layer's signed CloudFront vector tiles
 *     (`Policy`, `Signature`, `Key-Pair-Id`, `Expires`), fetched by MapLibre —
 *     plus the manifest and any sidecar the same signer covers;
 *   - the place-list CSV upload's S3 pre-signed `PUT` (`X-Amz-Signature`,
 *     `X-Amz-Credential`, `X-Amz-Security-Token`) in `placeUploadClient`.
 * Without this the credential itself is what gets shipped to a third party and
 * retained there.
 *
 * NO PARAMETER NAME IS MATCHED ANYWHERE IN THIS MODULE. A parameter counts as
 * signing material by the SHAPE of its VALUE — an opaque token long enough to
 * be a signature (see {@link isCredentialShaped}) — so rotating to a different
 * CDN, moving to another storage provider, or renaming a parameter cannot
 * quietly reopen the leak. It also means an ordinary `?policy=refund` is not a
 * credential and survives untouched.
 *
 * What this deliberately does NOT cover: a credential carried in the PATH
 * rather than the query or fragment, and short public identifiers that are not
 * secrets (CloudFront's `Key-Pair-Id`, an `Expires` timestamp) — those stay
 * readable in error text, which is what makes a trace diagnosable.
 *
 * A SECOND, name-agnostic rule covers our own APIs: on a URL whose origin is
 * the Constellation or Cosmic API, EVERY query value is replaced (see
 * {@link redactApiQueryValues}). Those query strings carry customer data —
 * place search terms, addresses, filter values (`search=…`, `name.in=…`) —
 * that is not credential-shaped and so survives the rule above.
 */

import { apiOrigin } from "@/utils/apiOrigins";

/**
 * Shortest value treated as signing material.
 *
 * Every signature in play is far longer: SigV4 is 64 hex characters, a
 * CloudFront policy is a base64 blob, an Azure SAS `sig` is 44. Nothing under
 * this length is cryptographic, so the floor is what keeps ordinary query
 * values (`?limit=100`, `?policy=refund`, `?tab=details`) out of the net.
 */
const MIN_CREDENTIAL_LENGTH = 32;

/** The alphabet a signature/token can use once percent-escapes are decoded. */
const OPAQUE_TOKEN = /^[A-Za-z0-9+/=_.~:-]+$/;

/**
 * A UUID is an opaque identifier of credential length but is never a secret,
 * and this app puts them in query strings routinely. Excluding the canonical
 * shape keeps `?organizationId=<uuid>` legible in RUM.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function decodeIfPossible(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A lone `%` or a truncated escape. The raw text is still what we have to
    // judge, and judging it is safer than skipping the check.
    return value;
  }
}

/**
 * Does this query VALUE look like signing material?
 *
 * The test is deliberately about shape, not about the key it sits under.
 * A signature is long, opaque, and mixes letters with digits; prose, paths,
 * timestamps and identifiers fail at least one of those.
 */
function isCredentialShaped(value: string): boolean {
  const decoded = decodeIfPossible(value);

  if (decoded.length < MIN_CREDENTIAL_LENGTH) return false;
  if (!OPAQUE_TOKEN.test(decoded)) return false;
  // A long run of digits is a timestamp or an id, not a signature; a long run
  // of letters is a word or a path segment. Real signatures are base64/hex and
  // carry both.
  if (!/[A-Za-z]/.test(decoded)) return false;
  if (!/[0-9]/.test(decoded)) return false;
  if (UUID.test(decoded)) return false;

  return true;
}

/** Splits `?a=1&b=2` (or a `#a=1&b=2` fragment) into raw, still-encoded values. */
function rawParamValues(component: string): string[] {
  const body = component.replace(/^[?#]/, "");
  if (!body) return [];

  return body.split("&").flatMap((pair) => {
    const separator = pair.indexOf("=");
    return separator === -1 ? [] : [pair.slice(separator + 1)];
  });
}

/**
 * Drops the query and fragment from any URL that carries signing material.
 *
 * The path is deliberately kept — `/{analysis}/pressure/{param}/{z}/{x}/{y}.mvt`
 * or an S3 bucket/key holds no secret and is what makes a slow- or failed-fetch
 * trace readable at all.
 *
 * A `.mvt` path is redacted unconditionally: every tile this app fetches is
 * signed by construction, so there is nothing to detect and no value in the
 * query worth keeping. Every other URL is judged on the shape of its values,
 * which is what covers the S3 pre-signed upload, the tile manifest, and any
 * signed sidecar without naming a single parameter.
 */
export function redactSignedTileUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Not a URL at all — a relative path, a placeholder, an already-mangled
    // string. Returning it unchanged is safer than inventing a base origin,
    // which would fabricate a host that was never contacted.
    return url;
  }

  const isTile = parsed.pathname.toLowerCase().endsWith(".mvt");
  const isSigned =
    isTile ||
    [...rawParamValues(parsed.search), ...rawParamValues(parsed.hash)].some(
      isCredentialShaped,
    );

  if (!isSigned) return url;

  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

/** What a redacted query value is replaced with. */
const REDACTED = "REDACTED";

/** The Constellation and Cosmic API origins, read from env at call time. */
function configuredApiOrigins(): string[] {
  return [apiOrigin("constellation"), apiOrigin("cosmic")].filter(
    (origin): origin is string => origin !== null,
  );
}

/**
 * Replaces every non-empty query VALUE with `REDACTED`, and drops the
 * fragment, on a URL whose origin is one of `apiOrigins`; any other URL is
 * returned unchanged.
 *
 * Scope: this runs on the event's URL FIELDS only (`resource.url`,
 * `error.resource.url`). An API URL quoted inside error TEXT (message, stack,
 * causes) still gets only the signed-parameter scrub.
 *
 * Parameter NAMES are kept — they are our API's vocabulary, not customer
 * data, and they are what makes a slow-request trace readable. The path is
 * kept too: the authenticated API paths carry record ids, not customer data
 * (unlike, say, the public tracking route's IMb, which encodes a delivery
 * ZIP — that route is outside the RUM-instrumented layout). The query is
 * rewritten from its raw text rather than through `URLSearchParams`, so names
 * keep their exact on-the-wire spelling.
 */
export function redactApiQueryValues(
  url: string,
  apiOrigins: readonly string[] = configuredApiOrigins(),
): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (parsed.origin === "null" || !apiOrigins.includes(parsed.origin)) {
    return url;
  }
  if (!parsed.search && !parsed.hash) return url;
  parsed.hash = "";
  if (!parsed.search) return parsed.toString();

  parsed.search = parsed.search
    .slice(1)
    .split("&")
    .map((pair) => {
      const separator = pair.indexOf("=");
      if (separator === -1 || separator === pair.length - 1) return pair;
      return `${pair.slice(0, separator)}=${REDACTED}`;
    })
    .join("&");
  return parsed.toString();
}

/**
 * A `?`/`&`-introduced `key=value` pair as it appears inside prose.
 *
 * The VALUE is matched with the token alphabet (plus `%` for escapes) rather
 * than "everything up to a delimiter". A stack frame wraps the URL in
 * parentheses — `at putPlaceCsvToPresignedUrl (https://…&X-Amz-Signature=abc)`
 * — and a delimiter-based class swallows the closing `)`, which then makes the
 * value fail {@link isCredentialShaped} and leaks the signature verbatim.
 * Stopping at the first character a token cannot contain leaves the trailing
 * punctuation where it was and judges the token itself.
 *
 * Written as a regex LITERAL on purpose: built from a template literal, a `\s`
 * collapses to a plain `s`, so any class relying on one silently ends the match
 * at the first "s" INSIDE the signature.
 */
const QUERY_PAIR_PATTERN = /([?&])([^=&#\s"']+)=([A-Za-z0-9%+/=_.~:-]*)/g;

/**
 * Scrubs signing material out of arbitrary TEXT.
 *
 * {@link redactSignedTileUrl} only helps where the URL is a field of its own.
 * A fetch that fails arrives as an error whose MESSAGE embeds the URL —
 * `mapbox-gl` builds `AJAXError` as `"<statusText> (<status>): <url>"` — and an
 * expiring signature guarantees that path is taken eventually, since `Expires`
 * is the point. Enumerating fields loses to that; scrubbing the text does not.
 *
 * Text cannot have its "whole query" dropped the way a parsed URL can — there
 * is no reliable end delimiter — so this replaces the credential-shaped VALUES
 * and keeps their keys. A redacted message still shows that a signature was
 * present, which is what makes the trace readable.
 */
export function redactSignedParams(text: string): string {
  return text.replace(
    QUERY_PAIR_PATTERN,
    (match, prefix: string, key: string, value: string) =>
      isCredentialShaped(value) ? `${prefix}${key}=REDACTED` : match,
  );
}

/** The subset of a Datadog RUM event this redaction touches. */
export interface RedactableRumEvent {
  type: string;
  resource?: { url?: string };
  error?: {
    message?: string;
    stack?: string;
    resource?: { url?: string };
    /** Datadog flattens an Error's `cause` chain here — each link carries its
     * own message and stack, which leak exactly like the top-level ones. */
    causes?: { message?: string; stack?: string }[];
  };
}

/**
 * Applies every redaction to one RUM event, in place, and answers whether the
 * event may ship at all.
 *
 * Extracted from `DataDogRum`'s `beforeSend` so the WIRING is testable, not
 * just the string helpers. The event-type discriminators, the optional-chain
 * guards, and which fields are covered at all are the parts that decide whether
 * a credential ships — and none of that is exercised by unit tests on the
 * helpers alone.
 *
 * Returns `false` — DROP the event — when a signed parameter sits in a field
 * the SDK will not persist edits to: `@datadog/browser-rum-core` runs
 * `beforeSend` on a clone and copies back only `error.message`, `error.stack`,
 * `error.resource.url`, and `error.fingerprint` (assembly.js), so an edit to
 * `error.causes[]` is silently discarded and the only way to keep a signed
 * cause out of intake is to withhold the whole event.
 */
export function redactRumEvent(
  event: RedactableRumEvent,
  apiOrigins: readonly string[] = configuredApiOrigins(),
): boolean {
  // Signed-URL logic first (it may drop the query outright), then the API
  // rule scrubs whatever query values remain on our own origins.
  const redactUrl = (url: string) =>
    redactApiQueryValues(redactSignedTileUrl(url), apiOrigins);

  if (event.type === "resource" && event.resource?.url) {
    event.resource.url = redactUrl(event.resource.url);
  }
  if (event.type === "error" && event.error) {
    if (event.error.resource?.url) {
      event.error.resource.url = redactUrl(event.error.resource.url);
    }
    if (event.error.message) {
      event.error.message = redactSignedParams(event.error.message);
    }
    if (event.error.stack) {
      event.error.stack = redactSignedParams(event.error.stack);
    }
    if (Array.isArray(event.error.causes)) {
      for (const cause of event.error.causes) {
        if (
          (cause?.message &&
            redactSignedParams(cause.message) !== cause.message) ||
          (cause?.stack && redactSignedParams(cause.stack) !== cause.stack)
        ) {
          return false;
        }
      }
    }
  }
  return true;
}
